import React from "react";
import useStudentPortalFetch, {
  StudentPortalShell,
  StudentPortalState,
  formatPortalDate,
  formatPortalTime,
} from "./useStudentPortalFetch";
import useStudentCourseSelection from "./useStudentCourseSelection";
import StudentCourseFilter from "./StudentCourseFilter";

function splitDateTime(value, fallbackDate) {
  const s = value != null && value !== "" ? String(value).trim() : "";
  const datePart =
    (s ? formatPortalDate(s) : null) ||
    formatPortalDate(fallbackDate) ||
    null;
  const timeMatch = s.match(/(\d{1,2}:\d{2}(?::\d{2})?)/);
  const timePart = timeMatch ? formatPortalTime(timeMatch[1]) : "";
  return { datePart, timePart };
}

function ExternalLink({ href, children }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="ems-student-link"
    >
      {children}
    </a>
  );
}

export default function StudentClassRecordsPage() {
  const { data, loading, error } = useStudentPortalFetch("class-records");
  const {
    options,
    selectedAdmissionId,
    setSelectedAdmissionId,
    showFilter,
    loading: coursesLoading,
    filterRows,
  } = useStudentCourseSelection();
  const rows = filterRows(Array.isArray(data) ? data : []);

  return (
    <StudentPortalShell
      title="My Schedule"
      subtitle="Recorded classes for your batches, with duration and watch links."
      headerRight={
        <StudentCourseFilter
          options={options}
          value={selectedAdmissionId}
          onChange={setSelectedAdmissionId}
          showFilter={showFilter}
          loading={coursesLoading}
        />
      }
    >
      <StudentPortalState
        loading={loading}
        error={error}
        empty={!loading && !error && rows.length === 0}
        emptyText={
          showFilter && selectedAdmissionId !== "all"
            ? "No class records for this course yet."
            : undefined
        }
      >
        <section className="ems-student-section">
          <div className="ems-student-table-wrap">
            <table className="ems-student-table">
              <thead>
                <tr>
                  <th>Topic</th>
                  <th>Batch</th>
                  <th>When</th>
                  <th>Duration</th>
                  <th>Status</th>
                  <th>Watch</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const { datePart, timePart } = splitDateTime(
                    row.start_time,
                    row.class_date
                  );

                  return (
                    <tr key={row.organization_ems_batch_class_record_id}>
                      <td data-label="Topic">
                        <div className="ems-student-program-name">
                          <span>{row.topic || "Class session"}</span>
                        </div>
                        {row.training_program_name ? (
                          <p className="ems-student-meta">
                            {row.training_program_name}
                          </p>
                        ) : null}
                        {row.mode ? (
                          <p className="ems-student-meta">Mode: {row.mode}</p>
                        ) : null}
                      </td>
                      <td className="ems-student-nowrap" data-label="Batch">
                        {row.batch_name || "—"}
                      </td>
                      <td data-label="When">
                        {datePart || timePart ? (
                          <div className="ems-student-datetime-cell">
                            <span className="ems-student-nowrap">
                              {datePart || "—"}
                            </span>
                            {timePart ? (
                              <p className="ems-student-meta">{timePart}</p>
                            ) : null}
                          </div>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="ems-student-nowrap" data-label="Duration">
                        {row.duration || "—"}
                      </td>
                      <td className="ems-student-nowrap" data-label="Status">
                        {row.status || "—"}
                      </td>
                      <td className="ems-student-nowrap" data-label="Watch">
                        {row.recording_url ? (
                          <ExternalLink href={row.recording_url}>
                            Watch recording
                          </ExternalLink>
                        ) : (
                          <span className="ems-student-meta">No recording</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </StudentPortalState>
    </StudentPortalShell>
  );
}
